import { createProgram } from "./program.js";
import { createTarget } from "./targets.js";
import geometryVertex from "./shaders/fill-geometry.vert";
import quadVertex from "./shaders/fill-quad.vert";
import coverageFragment from "./shaders/fill-coverage.frag";
import watercolorFragment from "./shaders/fill-watercolor.frag";

const STENCIL_MASK = 0xff;
const IDENTITY = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

function unit(value, fallback) {
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : fallback;
}

function dimension(value) {
  if (!Number.isFinite(value) || value <= 0) throw new RangeError("GPU fill dimensions must be positive and finite.");
  return Math.max(1, Math.round(value));
}

function uniforms(gl, program, names) {
  return Object.fromEntries(names.map((name) => [name, gl.getUniformLocation(program, name)]));
}

/**
 * GPU watercolor coverage, independent of p5, Canvas2D, and the old fill core.
 *
 * The enclosing engine owns GL state capture/restoration. All dimensions and
 * transform output coordinates are physical pixels, with logical y=0 stored at
 * texture texel y=0. draw() writes premultiplied red/alpha into a borrowed mask.
 *
 * command = { paths: [[[x, y], ...], ...], opacity, bleed, texture, border,
 *   scatter, direction: "in"|"out"|"both", angle: radians|null, seed,
 *   transform: {a,b,c,d,e,f}, resolution: 1, mode: "fill"|"wash", color,
 *   clip: {inverse: [a,b,c,d,x,y], bounds: [x1,y1,x2,y2]}|null }
 *
 * opacity/bleed/texture/border are normalized [0,1]. color is deliberately left
 * to the enclosing pigment compositor; this module only generates its mask.
 */
export class GpuFill {
  constructor(gl, width, height) {
    if (!gl?.createVertexArray) throw new Error("GpuFill requires WebGL2.");
    this.gl = gl;
    this.width = dimension(width);
    this.height = dimension(height);
    this._coverage = null;
    this._geometryProgram = null;
    this._coverageProgram = null;
    this._watercolorProgram = null;
    this._geometryVao = null;
    this._quadVao = null;
    this._buffer = null;
    this._vertices = new Float32Array(4096);
    this._clipMatrix = new Float32Array(9);
    this._destroyed = false;
    this.diagnostics = {
      shapes: 0,
      gpuDrawCalls: 0,
      cpuGeneratedVertices: 0,
      lastDrawCalls: 0,
      lastGeneratedVertices: 0,
      scratchBytes: this._vertices.byteLength,
    };
  }

  _ensureResources() {
    const gl = this.gl;
    if (!this._geometryProgram) {
      try {
        this._geometryProgram = createProgram(gl, geometryVertex, coverageFragment);
        this._coverageProgram = createProgram(gl, quadVertex, coverageFragment);
        this._watercolorProgram = createProgram(gl, quadVertex, watercolorFragment);
        this._geometryUniforms = uniforms(gl, this._geometryProgram, ["u_size"]);
        this._coverageUniforms = uniforms(gl, this._coverageProgram, ["u_size", "u_bounds"]);
        this._watercolorUniforms = uniforms(gl, this._watercolorProgram, [
          "u_size", "u_bounds", "u_baseBounds", "u_coverage", "u_controls", "u_flow", "u_resolution",
          "u_seed", "u_scatter", "u_direction", "u_wash",
          "u_clip", "u_clipInverse", "u_clipBounds",
        ]);
        this._geometryVao = gl.createVertexArray();
        this._quadVao = gl.createVertexArray();
        this._buffer = gl.createBuffer();
        if (!this._geometryVao || !this._quadVao || !this._buffer) {
          throw new Error("Could not allocate GPU fill geometry resources.");
        }
        gl.bindVertexArray(this._geometryVao);
        gl.bindBuffer(gl.ARRAY_BUFFER, this._buffer);
        const position = gl.getAttribLocation(this._geometryProgram, "a_position");
        gl.enableVertexAttribArray(position);
        gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 8, 0);
      } catch (error) {
        this._deleteResources();
        throw error;
      }
    }
    if (!this._coverage) this._coverage = createTarget(gl, this.width, this.height, { stencil: true });
  }

  _geometry(paths, matrix) {
    let count = 0;
    for (const path of paths) if (path.length >= 3) count += path.length;
    const floats = count * 6;
    if (this._vertices.length < floats) {
      let capacity = this._vertices.length;
      while (capacity < floats) capacity *= 2;
      this._vertices = new Float32Array(capacity);
      this.diagnostics.scratchBytes = this._vertices.byteLength;
    }
    const a = matrix.a ?? 1, b = matrix.b ?? 0, c = matrix.c ?? 0, d = matrix.d ?? 1;
    const e = matrix.e ?? matrix.x ?? 0, f = matrix.f ?? matrix.y ?? 0;
    if (![a, b, c, d, e, f].every(Number.isFinite)) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let offset = 0;
    for (const path of paths) {
      if (path.length < 3) continue;
      let centerX = 0, centerY = 0;
      for (const point of path) {
        if (!Number.isFinite(point[0]) || !Number.isFinite(point[1])) return null;
        centerX += point[0]; centerY += point[1];
      }
      centerX /= path.length; centerY /= path.length;
      const ox = a * centerX + c * centerY + e;
      const oy = b * centerX + d * centerY + f;
      for (let i = 0; i < path.length; i++) {
        const p = path[i], q = path[(i + 1) % path.length];
        const px = a * p[0] + c * p[1] + e, py = b * p[0] + d * p[1] + f;
        const qx = a * q[0] + c * q[1] + e, qy = b * q[0] + d * q[1] + f;
        this._vertices[offset++] = ox; this._vertices[offset++] = oy;
        this._vertices[offset++] = px; this._vertices[offset++] = py;
        this._vertices[offset++] = qx; this._vertices[offset++] = qy;
        minX = Math.min(minX, px); minY = Math.min(minY, py);
        maxX = Math.max(maxX, px); maxY = Math.max(maxY, py);
      }
    }
    return offset ? { count: offset / 2, minX, minY, maxX, maxY } : null;
  }

  _scissor(bounds) {
    const x = Math.max(0, Math.floor(bounds.minX));
    const y = Math.max(0, Math.floor(bounds.minY));
    const right = Math.min(this.width, Math.ceil(bounds.maxX));
    const bottom = Math.min(this.height, Math.ceil(bounds.maxY));
    this.gl.scissor(x, y, Math.max(0, right - x), Math.max(0, bottom - y));
  }

  _quad(program, locations, bounds) {
    const gl = this.gl;
    gl.useProgram(program);
    gl.bindVertexArray(this._quadVao);
    gl.uniform2f(locations.u_size, this.width, this.height);
    gl.uniform4f(locations.u_bounds, bounds.minX, bounds.minY, bounds.maxX, bounds.maxY);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  /** Returns the physical dirty bounds, or null for an empty/offscreen shape. */
  draw(command, maskTarget) {
    if (this._destroyed) throw new Error("GpuFill has been destroyed.");
    if (!maskTarget?.framebuffer) throw new TypeError("GPU fill needs a framebuffer mask target.");
    if (maskTarget.width !== this.width || maskTarget.height !== this.height) this.resize(maskTarget.width, maskTarget.height);
    this.diagnostics.lastDrawCalls = this.diagnostics.lastGeneratedVertices = 0;
    const opacity = unit(command.opacity, 1);
    if (!opacity || !command.paths?.length) return null;
    const geometry = this._geometry(command.paths, command.transform ?? IDENTITY);
    if (!geometry || geometry.minX === geometry.maxX || geometry.minY === geometry.maxY) return null;
    const bleed = unit(command.bleed, 0.07);
    const texture = unit(command.texture, 0.8), border = unit(command.border, 0.5);
    const wash = command.mode === "wash";
    const scatter = command.scatter !== false;
    const resolution = Number.isFinite(command.resolution) && command.resolution > 0 ? command.resolution : 1;
    const minSize = Math.min(geometry.maxX - geometry.minX, geometry.maxY - geometry.minY);
    const bleedDistance = bleed * Math.max(3 * resolution, minSize * 0.22);
    // Flat wash uses only the rasterized polygon, with no diffusion fringe.
    const padding = wash ? 0 : bleedDistance * 4.0 + (border * 7 + 4) * resolution;
    const bounds = {
      minX: Math.max(0, Math.floor(geometry.minX - padding)),
      minY: Math.max(0, Math.floor(geometry.minY - padding)),
      maxX: Math.min(this.width, Math.ceil(geometry.maxX + padding)),
      maxY: Math.min(this.height, Math.ceil(geometry.maxY + padding)),
    };
    if (bounds.maxX <= bounds.minX || bounds.maxY <= bounds.minY) return null;
    this._ensureResources();
    const gl = this.gl;
    gl.viewport(0, 0, this.width, this.height);
    gl.disable(gl.DEPTH_TEST); gl.disable(gl.CULL_FACE); gl.disable(gl.RASTERIZER_DISCARD);
    gl.enable(gl.SCISSOR_TEST);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this._coverage.framebuffer);
    // Linear coverage samples also reach the adjacent texel. Clear that fringe
    // to keep a previous, larger polygon from leaking into this shape's edge.
    this._scissor({
      minX: geometry.minX - 1, minY: geometry.minY - 1,
      maxX: geometry.maxX + 1, maxY: geometry.maxY + 1,
    });
    gl.colorMask(true, true, true, true);
    gl.stencilMask(STENCIL_MASK);
    gl.clearColor(0, 0, 0, 0); gl.clearStencil(0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);
    gl.enable(gl.STENCIL_TEST);
    gl.stencilFunc(gl.ALWAYS, 0, STENCIL_MASK);
    gl.stencilOpSeparate(gl.FRONT, gl.KEEP, gl.KEEP, gl.INCR_WRAP);
    gl.stencilOpSeparate(gl.BACK, gl.KEEP, gl.KEEP, gl.DECR_WRAP);
    gl.frontFace(gl.CCW);
    gl.disable(gl.BLEND);
    gl.colorMask(false, false, false, false);
    gl.useProgram(this._geometryProgram);
    gl.uniform2f(this._geometryUniforms.u_size, this.width, this.height);
    gl.bindVertexArray(this._geometryVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this._buffer);
    gl.bufferData(gl.ARRAY_BUFFER, this._vertices.subarray(0, geometry.count * 2), gl.STREAM_DRAW);
    gl.drawArrays(gl.TRIANGLES, 0, geometry.count);

    gl.stencilMask(0);
    gl.stencilFunc(gl.NOTEQUAL, 0, STENCIL_MASK);
    gl.stencilOp(gl.KEEP, gl.KEEP, gl.KEEP);
    gl.colorMask(true, true, true, true);
    this._quad(this._coverageProgram, this._coverageUniforms, geometry);

    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, maskTarget.framebuffer);
    this._scissor(bounds);
    gl.disable(gl.STENCIL_TEST);
    gl.enable(gl.BLEND);
    gl.blendEquation(gl.FUNC_ADD);
    gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(this._watercolorProgram);
    const u = this._watercolorUniforms;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindSampler(0, null);
    gl.bindTexture(gl.TEXTURE_2D, this._coverage.texture);
    gl.uniform1i(u.u_coverage, 0);
    gl.uniform4f(u.u_baseBounds, geometry.minX, geometry.minY, geometry.maxX, geometry.maxY);
    gl.uniform4f(u.u_controls, opacity, bleedDistance, texture, border);
    gl.uniform1f(u.u_resolution, resolution);
    const angle = command.angle;
    gl.uniform2f(u.u_flow, Number.isFinite(angle) ? Math.cos(angle) : 0, Number.isFinite(angle) ? Math.sin(angle) : 0);
    gl.uniform1ui(u.u_seed, (Number(command.seed) || 0) >>> 0);
    gl.uniform1i(u.u_scatter, scatter ? 1 : 0);
    gl.uniform1i(u.u_direction, command.direction === "in" ? 1 : command.direction === "both" ? 2 : 0);
    gl.uniform1i(u.u_wash, wash ? 1 : 0);
    const clip = command.clip;
    gl.uniform1i(u.u_clip, clip ? 1 : 0);
    if (clip) {
      const inverse = clip.inverse, matrix = this._clipMatrix;
      matrix[0] = inverse[0]; matrix[1] = inverse[1]; matrix[2] = 0;
      matrix[3] = inverse[2]; matrix[4] = inverse[3]; matrix[5] = 0;
      matrix[6] = inverse[4]; matrix[7] = inverse[5]; matrix[8] = 1;
      gl.uniformMatrix3fv(u.u_clipInverse, false, matrix);
      const bounds = clip.bounds;
      gl.uniform4f(u.u_clipBounds, Math.min(bounds[0], bounds[2]), Math.min(bounds[1], bounds[3]),
        Math.max(bounds[0], bounds[2]), Math.max(bounds[1], bounds[3]));
    }
    this._quad(this._watercolorProgram, u, bounds);
    this.diagnostics.shapes++;
    this.diagnostics.gpuDrawCalls += 3;
    this.diagnostics.cpuGeneratedVertices += geometry.count;
    this.diagnostics.lastDrawCalls = 3;
    this.diagnostics.lastGeneratedVertices = geometry.count;
    return bounds;
  }

  resize(width, height) {
    if (this._destroyed) throw new Error("GpuFill has been destroyed.");
    width = dimension(width); height = dimension(height);
    if (width === this.width && height === this.height) return;
    this.width = width; this.height = height;
    this._coverage?.destroy();
    this._coverage = null;
  }

  _deleteResources() {
    const gl = this.gl;
    this._coverage?.destroy(); this._coverage = null;
    for (const key of ["_geometryProgram", "_coverageProgram", "_watercolorProgram"]) {
      if (this[key]) gl.deleteProgram(this[key]);
      this[key] = null;
    }
    for (const key of ["_geometryVao", "_quadVao"]) {
      if (this[key]) gl.deleteVertexArray(this[key]);
      this[key] = null;
    }
    if (this._buffer) gl.deleteBuffer(this._buffer);
    this._buffer = null;
  }

  destroy() {
    if (this._destroyed) return;
    this._deleteResources();
    this._vertices = new Float32Array(0);
    this._clipMatrix = null;
    this.diagnostics.scratchBytes = 0;
    this._destroyed = true;
  }
}
